import './styles.css';
import {
  enhancePublicForm,
  enhanceTerritorySettings,
  startAdminPolling,
  stopAdminPolling,
  territoryRowHtml,
} from './client-enhancements';
import {
  enhanceApplicationWorkflow,
  type PendingApplication,
} from './admin-workflow-enhancements';
import { validateClientStep } from './client-public-validation';

type Territory = {
  id: string;
  label: string;
  isTest?: boolean;
  active?: number;
  is_test?: number;
};
type Config = {
  territories: Territory[];
  productionReady: boolean;
  environment: string;
  turnstileSiteKey: string;
};
type AppRow = Record<string, any>;

const root = document.querySelector<HTMLDivElement>('#app')!;
let publicConfigError = '';
const legalForms = ['Entreprise individuelle', 'Micro-entreprise', 'EURL', 'SARL', 'SASU', 'SAS', 'SA', 'Association', 'Autre'];
const providers = ['Lydia', 'SumUp', 'Stripe', 'PayPal', 'Autre'];
const steps = [
  'Choix du territoire',
  'Informations du Dispatcher',
  'Organisation des courses',
  'Paramètres commerciaux',
  'Paiement des commissions',
  'Récapitulatif final',
];
let cfg: Config = {
  territories: [],
  productionReady: false,
  environment: 'development',
  turnstileSiteKey: '',
};
let current = 0;
let form: Record<string, any> = {
  territoryId: '',
  companyName: '',
  legalForm: '',
  siret: '',
  vatNumber: '',
  address: '',
  displayName: '',
  representativeName: '',
  phone: '',
  email: '',
  professionalPhone: '',
  priorityEnabled: null,
  joeDriverPhone: '',
  circleDrivers: '',
  networkExpansionAllowed: null,
  pricePerKmTtc: '',
  minimumPriceTtc: '',
  partnerCommissionPercent: '',
  minimumCommissionTtc: '',
  paymentProvider: '',
  partnerPaymentUrl: '',
  certification: false,
};

const esc = (value: unknown) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;',
}[char]!));
const val = (id: string) => (
  document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null
)?.value ?? '';

async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('json')) {
    await response.text();
    throw new Error(response.ok ? 'Réponse API invalide.' : `Erreur ${response.status}`);
  }
  const data = await response.json();
  if (!response.ok) {
    throw Object.assign(new Error((data as any)?.error || `Erreur ${response.status}`), {
      details: (data as any)?.details,
    });
  }
  return data as T;
}

function isPublicConfig(value: unknown): value is Config {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<Config>;
  return Array.isArray(candidate.territories) &&
    candidate.territories.every((territory) => (
      territory && typeof territory.id === 'string' && typeof territory.label === 'string'
    )) &&
    typeof candidate.productionReady === 'boolean' &&
    typeof candidate.environment === 'string' &&
    typeof candidate.turnstileSiteKey === 'string';
}

function shell(body: string, admin = false): string {
  return `<div class="app-shell"><header class="topbar"><a class="brand" href="${admin ? '/' : '#/'}"><img src="/logo_joe.svg" alt="Joe"></a><div class="topbar-meta">${admin ? 'Espace Joe' : 'Partenaires Dispatcher'}</div>${admin ? '<button id="logoutBtn" class="ghost small">Déconnexion</button>' : ''}</header><main>${body}</main><footer>JOE SASU · 254 rue Vendôme, 69003 Lyon · contact@joe.taxi</footer></div>`;
}

function field(label: string, id: string, opt: any = {}): string {
  const type = opt.type || 'text';
  const required = opt.required ? ' required aria-required="true"' : '';
  return `<label class="field"><span>${esc(label)}${opt.required ? ' <b>*</b>' : ''}</span><div class="input-wrap"><input id="${id}" type="${type}" value="${esc(form[id] ?? '')}"${required} ${opt.placeholder ? `placeholder="${esc(opt.placeholder)}"` : ''} ${type === 'number' ? 'min="0" step="0.01" inputmode="decimal"' : ''}>${opt.suffix ? `<em>${esc(opt.suffix)}</em>` : ''}</div>${opt.hint ? `<small>${opt.hint}</small>` : ''}</label>`;
}

function select(
  label: string,
  id: string,
  items: Array<string | { value: string; label: string }>,
  required = false,
): string {
  const selected = String(form[id] ?? '');
  return `<label class="field"><span>${esc(label)}${required ? ' <b>*</b>' : ''}</span><select id="${id}"${required ? ' required aria-required="true"' : ''}><option value="">Veuillez sélectionner</option>${items.map((item) => {
    const option = typeof item === 'string' ? { value: item, label: item } : item;
    return `<option value="${esc(option.value)}" ${option.value === selected ? 'selected' : ''}>${esc(option.label)}</option>`;
  }).join('')}</select></label>`;
}

function radios(label: string, id: string, hint = ''): string {
  return `<fieldset class="field radio-field"><legend>${esc(label)} <b>*</b></legend>${hint ? `<small>${hint}</small>` : ''}<div class="radio-row"><label><input type="radio" name="${id}" value="Oui" required aria-required="true" ${form[id] === true ? 'checked' : ''}><span>Oui</span></label><label><input type="radio" name="${id}" value="Non" required aria-required="true" ${form[id] === false ? 'checked' : ''}><span>Non</span></label></div></fieldset>`;
}

function money(value: any): string {
  return value === '' || value == null
    ? 'Non renseigné'
    : `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} € TTC`;
}

function status(value: string): string {
  const labels: Record<string, string> = {
    SUBMITTED: 'À étudier',
    REJECTED: 'Refusée',
    APPROVED_WAITING_SIGNATURE: 'Acceptée — préparation',
    SIGNATURE_SENT: 'Signature attendue',
    SIGNED: 'Signée',
    SECURE_LINK_SENDING: 'Envoi du lien…',
    SECURE_LINK_SENT: 'Terminée',
    SIGNATURE_DECLINED: 'Signature refusée',
    SIGNATURE_EXPIRED: 'Signature expirée',
    EXPIRED_AFTER_SIGNATURE: 'Lien expiré',
  };
  return `<span class="status status-${esc(value.toLowerCase())}">${esc(labels[value] || value)}</span>`;
}

function savePage(): void {
  document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input,select,textarea').forEach((element) => {
    if (element.type === 'radio') {
      if ((element as HTMLInputElement).checked) form[element.getAttribute('name') || ''] = element.value === 'Oui';
    } else if (element.type === 'checkbox') {
      form[element.id] = (element as HTMLInputElement).checked;
    } else if (element.id) {
      form[element.id] = element.value;
    }
  });
  if (form.priorityEnabled === false) form.joeDriverPhone = '';
}

function review(): string {
  const territory = cfg.territories.find((item) => item.id === form.territoryId)?.label || form.territoryId;
  const sections: any[] = [
    ['Territoire', [['Territoire', territory]]],
    ['Informations du Dispatcher', [
      ['Raison sociale', form.companyName],
      ['Forme juridique', form.legalForm],
      ['SIRET', form.siret],
      ['TVA', form.vatNumber || 'Non renseigné'],
      ['Adresse', form.address],
      ['Enseigne', form.displayName],
      ['Représentant', form.representativeName],
      ['Téléphone', form.phone],
      ['E-mail', form.email],
      ['Numéro professionnel', form.professionalPhone],
    ]],
    ['Organisation des courses', [
      ['Priorité', form.priorityEnabled ? 'Oui' : 'Non'],
      ['Téléphone Joe Driver', form.priorityEnabled ? (form.joeDriverPhone || 'Non renseigné') : 'Non applicable'],
      ['Cercle', form.circleDrivers || 'Non renseigné'],
      ['Élargissement Réseau Joe', form.networkExpansionAllowed ? 'Oui' : 'Non'],
    ]],
    ['Paramètres commerciaux', [
      ['Prix kilométrique', money(form.pricePerKmTtc)],
      ['Prix minimum', money(form.minimumPriceTtc)],
      ['Commission', `${form.partnerCommissionPercent || 0} %`],
      ['Minimum commission', money(form.minimumCommissionTtc)],
    ]],
    ['Paiement des commissions', [
      ['Prestataire', form.paymentProvider],
      ['Lien de paiement', form.partnerPaymentUrl],
    ]],
  ];
  return `<div class="review">${sections.map(([heading, rows]) => `<section><h2>${heading}</h2>${rows.map(([key, value]: string[]) => `<div><span>${esc(key)}</span><strong>${esc(value)}</strong></div>`).join('')}</section>`).join('')}</div>`;
}

function page(): string {
  if (current === 0) {
    return `<h1>Votre territoire</h1><p class="lead">Renseignez vos informations et paramètres pour soumettre votre candidature.</p>${cfg.environment !== 'production' ? `<div class="warning">Environnement ${esc(cfg.environment)} — utilisez uniquement des données de test.</div>` : ''}${publicConfigError ? `<div class="warning">${esc(publicConfigError)}</div>` : ''}${select('Quel territoire souhaitez-vous développer ?', 'territoryId', cfg.territories.map((territory) => ({ value: territory.id, label: territory.label })), true)}<div class="legal-note">Votre demande constitue une candidature. Joe reste libre de l’accepter ou de la refuser. La Zone de référence n’est pas exclusive et ne constitue aucun droit acquis sur un territoire.</div><div class="legal-note"><strong>Données personnelles :</strong> les candidatures sans convention sont anonymisées après 90 jours sans traitement, ou 2 ans après un refus. Les conventions signées sont archivées séparément pendant 10 ans. Pour exercer vos droits, contactez <a href="mailto:contact@joe.taxi">contact@joe.taxi</a>.</div>`;
  }
  if (current === 1) {
    return `<h1>Informations du Dispatcher</h1><div class="grid2">${field('Raison sociale', 'companyName', { required: true })}${select('Forme juridique', 'legalForm', legalForms, true)}${field('SIRET', 'siret', { required: true, placeholder: '14 chiffres', hint: 'Saisissez un SIRET français valide : 14 chiffres avec clé de contrôle.' })}${field('Numéro de TVA intracommunautaire', 'vatNumber')}${field('Adresse complète', 'address', { required: true })}${field('Nom affiché / enseigne', 'displayName', { required: true })}${field('Nom et prénom du représentant', 'representativeName', { required: true })}${field('Téléphone', 'phone', { required: true, placeholder: '0612345678' })}${field('E-mail de facturation / contact', 'email', { type: 'email', required: true })}${field('Numéro professionnel', 'professionalPhone', { required: true, placeholder: '0612345678' })}</div>`;
  }
  if (current === 2) {
    return `<h1>Organisation des courses</h1><div class="flow-note">Vous pendant 30 sec. → Votre Cercle → Réseau Joe si vous l’autorisez</div>${radios('Souhaitez-vous recevoir vos propres courses en priorité ?', 'priorityEnabled', 'Si Oui, la course vous est proposée à vous seul pendant 30 secondes avant d’être diffusée à votre Cercle.')}${form.priorityEnabled === true ? field('Numéro de téléphone associé à votre compte Joe Driver', 'joeDriverPhone', { required: true, placeholder: '0612345678' }) : ''}<label class="field"><span>Votre Cercle de chauffeurs</span><textarea id="circleDrivers" rows="5">${esc(form.circleDrivers)}</textarea><small>Indiquez les numéros associés aux comptes Joe Driver des chauffeurs de votre Cercle, un numéro par ligne.</small></label>${radios('Si aucun chauffeur de votre Cercle n’accepte la course, autorisez-vous Joe à la proposer à d’autres chauffeurs Joe ?', 'networkExpansionAllowed')}`;
  }
  if (current === 3) {
    return `<h1>Paramètres commerciaux</h1><div class="grid2">${field('Prix kilométrique TTC (€)', 'pricePerKmTtc', { type: 'number', required: true, suffix: '€ TTC' })}${field('Prix minimum TTC (€)', 'minimumPriceTtc', { type: 'number', required: true, suffix: '€ TTC' })}${field('Commission Partenaire (%)', 'partnerCommissionPercent', { type: 'number', required: true, suffix: '%' })}${field('Minimum de commission TTC (€)', 'minimumCommissionTtc', { type: 'number', suffix: '€ TTC' })}</div><div class="legal-note"><strong>Frais Joe :</strong> 3 % HT du prix final TTC de chaque course terminée, avec un minimum de 2,50 € HT par course terminée. Aucun Frais Joe lorsqu’aucune course n’est terminée. La Commission Partenaire est distincte des Frais Joe.</div>`;
  }
  if (current === 4) {
    return `<h1>Recevoir vos commissions</h1>${select('Prestataire de paiement de la Commission Partenaire', 'paymentProvider', providers, true)}${field('Lien de paiement de la Commission Partenaire', 'partnerPaymentUrl', { type: 'url', required: true, placeholder: 'paypal.me/votre-nom', hint: 'Le protocole http:// ou https:// est facultatif. Ce lien sera transmis au chauffeur après la course afin qu’il puisse vous régler votre commission.' })}`;
  }
  return `<h1>Récapitulatif final</h1><p class="lead">Vérifiez les informations renseignées</p>${review()}<label class="cert"><input id="certification" type="checkbox" required aria-required="true" ${form.certification ? 'checked' : ''}><span>Je certifie que les informations renseignées sont exactes et j’ai compris que ma candidature reste soumise à validation manuelle par Joe.</span></label>${cfg.turnstileSiteKey ? `<div class="turnstile-box"><div id="turnstile-widget"></div></div>` : '<div class="warning">La vérification anti-spam est temporairement indisponible. Réessayez plus tard.</div>'}`;
}

type TurnstileApi = {
  render: (container: HTMLElement, options: { sitekey: string; callback: (token: string) => void; 'expired-callback': () => void; 'error-callback': () => void }) => string;
  reset: (widgetId?: string) => void;
};

declare global { interface Window { turnstile?: TurnstileApi; } }

let turnstileToken = '';
let turnstileWidgetId = '';

function renderTurnstile(): void {
  const container = document.getElementById('turnstile-widget');
  if (!container || !cfg.turnstileSiteKey) return;
  const mount = () => {
    if (!window.turnstile || !document.body.contains(container)) return;
    turnstileWidgetId = window.turnstile.render(container, {
      sitekey: cfg.turnstileSiteKey,
      callback: (token) => { turnstileToken = token; },
      'expired-callback': () => { turnstileToken = ''; },
      'error-callback': () => { turnstileToken = ''; },
    });
  };
  if (window.turnstile) return mount();
  const script = document.createElement('script');
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  script.async = true;
  script.defer = true;
  script.addEventListener('load', mount, { once: true });
  document.head.append(script);
}

function renderForm(): void {
  root.innerHTML = shell(`<section class="form-page"><div class="form-card"><div class="progress-wrap"><div class="progress-meta"><span>Étape ${current + 1} sur 6</span><strong>${esc(steps[current])}</strong></div><div class="progress"><i style="width:${((current + 1) / 6) * 100}%"></i></div></div><div class="form-content">${page()}</div><div id="errs" class="errors"></div><div class="form-actions">${current ? '<button id="back" class="secondary">Retour</button>' : '<span></span>'}<button id="next" class="primary">${current === 5 ? 'Envoyer ma candidature' : 'Suivant'}</button></div></div></section>`);

  document.getElementById('back')?.addEventListener('click', () => {
    savePage();
    current -= 1;
    renderForm();
  });
  document.querySelectorAll<HTMLInputElement>('input[type=radio]').forEach((radio) => {
    radio.addEventListener('change', () => {
      savePage();
      if (radio.name === 'priorityEnabled') renderForm();
    });
  });
  document.getElementById('next')!.addEventListener('click', async () => {
    savePage();
    const errors = validateClientStep(current, form);
    if (errors.length) {
      document.getElementById('errs')!.innerHTML = errors.map((error) => `<div>${esc(error)}</div>`).join('');
      return;
    }
    if (current < 5) {
      current += 1;
      renderForm();
      return;
    }
    await submitApplication();
  });

  enhancePublicForm(current);
  if (current === 5) renderTurnstile();
}

async function submitApplication(): Promise<void> {
  const button = document.getElementById('next') as HTMLButtonElement;
  button.disabled = true;
  button.textContent = 'Envoi…';
  try {
    await api('/api/applications', {
      method: 'POST',
      body: JSON.stringify({ ...form, turnstileToken }),
    });
    root.innerHTML = shell('<section class="narrow"><div class="success-card"><div class="success-icon">✓</div><h1>Candidature transmise</h1><p>Votre candidature a bien été transmise à Joe. Elle va maintenant être étudiée. Vous serez informé de la décision de Joe par e-mail.</p></div></section>');
  } catch (error: any) {
    button.disabled = false;
    button.textContent = 'Envoyer ma candidature';
    turnstileToken = '';
    if (turnstileWidgetId) window.turnstile?.reset(turnstileWidgetId);
    document.getElementById('errs')!.innerHTML = `<div>${esc(error.message)}${error.details ? ` — ${esc(Array.isArray(error.details) ? error.details.join(' · ') : error.details)}` : ''}</div>`;
  }
}

async function renderAdmin(path: string): Promise<void> {
  if (path === '/admin' || path === '/admin/login') {
    renderLogin();
    return;
  }
  try {
    await api('/admin/api/admin/me');
  } catch {
    renderLogin();
    return;
  }
  if (path === '/admin/settings') {
    await renderSettings();
    return;
  }
  const match = path.match(/^\/admin\/application\/([^/]+)$/);
  if (match) {
    await renderDetail(decodeURIComponent(match[1]!));
    return;
  }
  await renderDashboard();
}

function navigate(path: string): void {
  window.history.pushState({}, '', path);
  void route();
}

function bindLogout(): void {
  document.getElementById('logoutBtn')?.addEventListener('click', async () => {
    await api('/admin/api/admin/logout', { method: 'POST' });
    navigate('/admin/login');
  });
}

function renderLogin(): void {
  root.innerHTML = shell('<section class="narrow"><div class="admin-login"><h1>Administration Joe</h1><p>Recevez un code temporaire sur l’adresse administrateur autorisée.</p><label class="field"><span>Adresse e-mail</span><input id="adminEmail" type="email" placeholder="administrateur@exemple.fr" autocomplete="email"></label><button id="sendCode" class="primary full">Recevoir un code</button><div id="loginStep"></div><div id="loginErr" class="errors"></div></div></section>');
  document.getElementById('sendCode')!.addEventListener('click', async () => {
    try {
      await api('/admin/api/admin/request-code', {
        method: 'POST',
        body: JSON.stringify({ email: val('adminEmail') }),
      });
      document.getElementById('loginStep')!.innerHTML = '<label class="field"><span>Code à 6 chiffres</span><input id="adminCode" inputmode="numeric" maxlength="6"></label><button id="verifyCode" class="primary full">Se connecter</button>';
      document.getElementById('verifyCode')!.addEventListener('click', async () => {
        try {
          await api('/admin/api/admin/verify-code', {
            method: 'POST',
            body: JSON.stringify({ email: val('adminEmail'), code: val('adminCode') }),
          });
          navigate('/admin/dashboard');
        } catch (error: any) {
          document.getElementById('loginErr')!.innerHTML = `<div>${esc(error.message)}</div>`;
        }
      });
    } catch (error: any) {
      document.getElementById('loginErr')!.innerHTML = `<div>${esc(error.message)}</div>`;
    }
  });
}

async function renderDashboard(): Promise<void> {
  root.innerHTML = shell('<section class="admin-page"><div class="admin-head"><div><p class="eyebrow">Joe Dispatcher</p><h1>Candidatures</h1></div><div class="head-actions"><a class="secondary button" href="/admin/api/admin/export.csv">Exporter CSV</a><a class="secondary button" href="/admin/settings">Paramètres</a></div></div><div id="adminContent" class="loading">Chargement…</div></section>', true);
  bindLogout();
  try {
    const data = await api<{ applications: AppRow[] }>('/admin/api/admin/applications');
    const applications = data.applications;
    const count = (state: string) => applications.filter((item) => item.status === state).length;
    document.getElementById('adminContent')!.innerHTML = `<div class="stats"><div><strong>${count('SUBMITTED')}</strong><span>À étudier</span></div><div><strong>${count('SIGNATURE_SENT')}</strong><span>Signature attendue</span></div><div><strong>${count('SECURE_LINK_SENT')}</strong><span>Terminées</span></div><div><strong>${applications.length}</strong><span>Total</span></div></div><div class="table-wrap"><table><thead><tr><th>Société</th><th>Territoire</th><th>Date</th><th>Statut</th><th></th></tr></thead><tbody>${applications.map((item) => `<tr><td><strong>${esc(item.company_name)}</strong><small>${esc(item.representative_name)}</small></td><td>${esc(item.territory_label)}</td><td>${esc(new Date(item.created_at).toLocaleString('fr-FR'))}</td><td>${status(item.status)}</td><td><a class="text-link" href="/admin/application/${esc(item.id)}">Ouvrir →</a></td></tr>`).join('') || '<tr><td colspan="5">Aucune candidature.</td></tr>'}</tbody></table></div>`;
  } catch (error: any) {
    document.getElementById('adminContent')!.innerHTML = `<div class="errors"><div>${esc(error.message)}</div></div>`;
  }
}

async function renderDetail(id: string): Promise<void> {
  stopAdminPolling();
  root.innerHTML = shell(`<section class="admin-page"><a class="back" href="/admin/dashboard">← Retour</a><div id="detail" class="loading">Chargement…</div></section>`, true);
  bindLogout();
  try {
    const data = await api<any>(`/admin/api/admin/applications/${encodeURIComponent(id)}`);
    const application = data.application;
    const rows = [
      ['Territoire', application.territory_label],
      ['Raison sociale', application.company_name],
      ['Forme juridique', application.legal_form],
      ['SIRET', application.siret],
      ['TVA', application.vat_number || 'Non renseigné'],
      ['Adresse', application.address],
      ['Enseigne', application.display_name],
      ['Représentant', application.representative_name],
      ['Téléphone', application.phone],
      ['E-mail', application.email],
      ['Numéro professionnel', application.professional_phone],
      ['Priorité', application.priority_enabled ? 'Oui' : 'Non'],
      ['Téléphone Joe Driver', application.priority_enabled ? (application.joe_driver_phone || 'Non renseigné') : 'Non applicable'],
      ['Cercle', application.circle_drivers || 'Non renseigné'],
      ['Réseau Joe', application.network_expansion_allowed ? 'Oui' : 'Non'],
      ['Prix/km', `${application.price_per_km_ttc} € TTC`],
      ['Prix minimum', `${application.minimum_price_ttc} € TTC`],
      ['Commission', `${application.partner_commission_percent} %`],
      ['Minimum commission', application.minimum_commission_ttc == null ? 'Non renseigné' : `${application.minimum_commission_ttc} € TTC`],
      ['Prestataire', application.payment_provider],
      ['Lien paiement commission', application.partner_payment_url],
      ['Rétention', application.anonymized_at ? `Anonymisée le ${new Date(application.anonymized_at).toLocaleDateString('fr-FR')}` : application.signed_pdf_key ? 'Archive contractuelle séparée (10 ans)' : 'Données opérationnelles'],
    ];
    const decision = application.status === 'SUBMITTED'
      ? `<div class="decision-card"><h2>Décision Joe</h2><p>Avant d’accepter : créez la fiche Dispatcher dans le dashboard Joe puis générez son lien sécurisé unique.</p><button id="rejectBtn" class="danger full">Refuser</button><hr><label class="field"><span>Lien sécurisé</span><input id="secureLink" type="url" placeholder="https://..."></label><label class="field"><span>Date/heure de génération</span><input id="generatedAt" type="datetime-local" value="${new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}"><small>Expiration automatique : +24 h.</small></label><button id="acceptBtn" class="primary full">Accepter et envoyer la convention</button><div id="decisionMsg" class="errors"></div></div>`
      : `<div class="decision-card"><h2>Suivi</h2><p>${status(application.status)}</p>${application.secure_link_generated_at ? `<p>Généré : <strong>${esc(new Date(application.secure_link_generated_at).toLocaleString('fr-FR'))}</strong><br>Expire : <strong>${esc(new Date(application.secure_link_expires_at).toLocaleString('fr-FR'))}</strong></p>` : ''}${application.signed_pdf_key ? `<a class="primary button full" href="/admin/api/admin/applications/${esc(id)}/signed-pdf">PDF signé + audit</a>` : ''}${['SIGNATURE_SENT', 'SIGNED'].includes(application.status) ? '<button id="finalizeBtn" class="secondary full">Vérifier/finaliser la signature</button>' : ''}<div id="decisionMsg" class="errors"></div></div>`;

    const detail = document.getElementById('detail')!;
    detail.innerHTML = `<div class="detail-head"><div><p class="eyebrow">Candidature</p><h1>${esc(application.company_name)}</h1></div>${status(application.status)}</div><div class="detail-grid"><div><div class="info-card">${rows.map(([key, value]) => `<div><span>${esc(key)}</span><strong>${esc(value)}</strong></div>`).join('')}</div><section class="audit-card"><h2>Journal d’audit</h2><ol>${(data.auditEvents || []).map((event: any) => `<li><strong>${esc(event.event_type)}</strong><span>${esc(new Date(event.created_at).toLocaleString('fr-FR'))} · ${esc(event.actor)}</span></li>`).join('')}</ol></section></div><aside>${decision}</aside></div>`;

    document.getElementById('rejectBtn')?.addEventListener('click', async () => {
      if (!confirm('Confirmer le refus ?')) return;
      await api(`/admin/api/admin/applications/${encodeURIComponent(id)}/reject`, { method: 'POST' });
      await renderDetail(id);
    });
    document.getElementById('acceptBtn')?.addEventListener('click', async () => {
      if (!confirm('Accepter et envoyer la convention V1.8 ?')) return;
      try {
        const local = val('generatedAt');
        await api(`/admin/api/admin/applications/${encodeURIComponent(id)}/accept`, {
          method: 'POST',
          body: JSON.stringify({ secureLink: val('secureLink'), generatedAt: new Date(local).toISOString() }),
        });
        await renderDetail(id);
      } catch (error: any) {
        document.getElementById('decisionMsg')!.innerHTML = `<div>${esc(error.message)}</div>`;
      }
    });
    document.getElementById('finalizeBtn')?.addEventListener('click', async () => {
      try {
        await api(`/admin/api/admin/applications/${encodeURIComponent(id)}/finalize-signature`, { method: 'POST' });
        await renderDetail(id);
      } catch (error: any) {
        document.getElementById('decisionMsg')!.innerHTML = `<div>${esc(error.message)}</div>`;
      }
    });

    enhanceApplicationWorkflow(detail, id, application as PendingApplication);
    startAdminPolling(id, String(application.status || ''), () => { void renderDetail(id); });
  } catch (error: any) {
    document.getElementById('detail')!.innerHTML = `<div class="errors"><div>${esc(error.message)}</div></div>`;
  }
}

async function renderSettings(): Promise<void> {
  root.innerHTML = shell('<section class="admin-page"><a class="back" href="/admin/dashboard">← Retour</a><div class="admin-head"><div><p class="eyebrow">Configuration</p><h1>Paramètres</h1></div></div><div id="settings" class="loading">Chargement…</div></section>', true);
  bindLogout();
  try {
    const [territoryData, contract] = await Promise.all([
      api<any>('/admin/api/admin/territories'),
      api<any>('/admin/api/admin/contract'),
    ]);
    const settings = document.getElementById('settings')!;
    settings.innerHTML = `<div class="settings-grid"><section class="settings-card"><h2>Convention V1.8</h2><p>${contract.exists ? '<span class="ok">✓ PDF configuré</span>' : '<span class="warn">PDF à téléverser</span>'}</p>${contract.sha256 ? `<code>${esc(contract.sha256)}</code>` : ''}<label class="upload"><input id="contractFile" type="file" accept="application/pdf"><span>Choisir le PDF définitif V1.8 (10 pages)</span></label><button id="uploadContract" class="primary">Téléverser</button><div id="contractMsg" class="errors"></div></section><section class="settings-card"><h2>Territoires</h2><div class="territory-list">${territoryData.territories.map((territory: any) => territoryRowHtml(territory)).join('')}</div><div class="inline-form"><input id="newTerritory" placeholder="Nouveau territoire"><button id="addTerritory" class="secondary">Ajouter</button></div></section></div>`;
    settings.classList.remove('loading');
    enhanceTerritorySettings();
    void import('./contract-editor')
      .then(({ mountContractEditor }) => mountContractEditor())
      .catch((error) => {
        const settings = document.getElementById('settings');
        if (settings) {
          settings.insertAdjacentHTML(
            'beforeend',
            `<div class="errors"><div>${esc(error instanceof Error ? error.message : 'Éditeur PDF indisponible.')}</div></div>`,
          );
        }
      });
  } catch (error: any) {
    document.getElementById('settings')!.innerHTML = `<div class="errors"><div>${esc(error.message)}</div></div>`;
  }
}

async function route(): Promise<void> {
  stopAdminPolling();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin' || path.startsWith('/admin/')) {
    await renderAdmin(path);
    return;
  }
  const hash = location.hash || '#/';
  if (hash === '#/signature-terminee') {
    root.innerHTML = shell('<section class="narrow"><div class="success-card"><div class="success-icon">✓</div><h1>Signature enregistrée</h1><p>Votre signature a été transmise. Le lien sécurisé vous sera envoyé automatiquement après confirmation de la signature, s’il est encore valide.</p></div></section>');
    return;
  }
  renderForm();
}

(async () => {
  if ((location.hash || '').startsWith('#/admin')) {
    const target = location.hash.slice(1) || '/admin';
    window.history.replaceState({}, '', target);
  }

  try {
    const candidate = await api<unknown>('/api/public/config');
    if (!isPublicConfig(candidate)) throw new Error('Configuration publique invalide.');
    cfg = candidate;
  } catch {
    publicConfigError = 'Le serveur API Worker n’est pas disponible. Lancez `wrangler dev` pour charger les territoires et envoyer une candidature.';
  }
  window.addEventListener('hashchange', () => { void route(); });
  await route();
})();
